import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { WheelPicker } from "./WheelPicker";

const MONTH_NAMES = [
  "Tháng 1",
  "Tháng 2",
  "Tháng 3",
  "Tháng 4",
  "Tháng 5",
  "Tháng 6",
  "Tháng 7",
  "Tháng 8",
  "Tháng 9",
  "Tháng 10",
  "Tháng 11",
  "Tháng 12",
];

const YEAR_RANGE_PAST = 100;
const YEAR_RANGE_FUTURE = 10;
const CURRENT_ACTUAL_YEAR = new Date().getFullYear();

export interface CalendarProps {
  selectedDate?: Date | null;
  currentMonth: number;
  currentYear: number;
  onDateSelect: (date: Date) => void;
  onNavigate: (direction: "prev" | "next") => void;
  onHeaderClick?: () => void;
  className?: string;
  headerClickable?: boolean;
}

const Calendar = React.forwardRef<HTMLDivElement, CalendarProps>(
  (
    {
      selectedDate,
      currentMonth,
      currentYear,
      onDateSelect,
      onNavigate,
      onHeaderClick,
      className,
      headerClickable = false,
    },
    ref,
  ) => {
    const [isMonthYearPickerOpen, setIsMonthYearPickerOpen] = useState(false);
    const [viewMonth, setViewMonth] = useState(currentMonth);
    const [viewYear, setViewYear] = useState(currentYear);
    const [tempMonth, setTempMonth] = useState(currentMonth);
    const [tempYear, setTempYear] = useState(currentYear);

    useEffect(() => {
      setViewMonth(currentMonth);
      setViewYear(currentYear);
      setTempMonth(currentMonth);
      setTempYear(currentYear);
    }, [currentMonth, currentYear]);

    const monthOptions = useMemo(
      () =>
        MONTH_NAMES.map((label, index) => ({
          label,
          value: String(index),
        })),
      [],
    );

    const yearOptions = useMemo(() => {
      const startYear = CURRENT_ACTUAL_YEAR - YEAR_RANGE_PAST;
      const endYear = CURRENT_ACTUAL_YEAR + YEAR_RANGE_FUTURE;
      return Array.from({ length: endYear - startYear + 1 }, (_, idx) => {
        const year = startYear + idx;
        return { label: String(year), value: String(year) };
      });
    }, []);

    const updateMonthYear = useCallback(
      (direction: "prev" | "next") => {
        let newMonth = direction === "prev" ? viewMonth - 1 : viewMonth + 1;
        let newYear = viewYear;

        if (newMonth < 0) {
          newMonth = 11;
          newYear -= 1;
        } else if (newMonth > 11) {
          newMonth = 0;
          newYear += 1;
        }

        setViewMonth(newMonth);
        setViewYear(newYear);
        onNavigate(direction);
      },
      [viewMonth, viewYear, onNavigate],
    );

    const handleMonthYearOpen = useCallback(() => {
      if (!headerClickable) return;
      if (onHeaderClick) {
        onHeaderClick();
        return;
      }
      setTempMonth(viewMonth);
      setTempYear(viewYear);
      setIsMonthYearPickerOpen(true);
    }, [headerClickable, onHeaderClick, viewMonth, viewYear]);

    const handleMonthYearCancel = useCallback(() => {
      setIsMonthYearPickerOpen(false);
    }, []);

    const handleMonthYearConfirm = useCallback(() => {
      setViewMonth(tempMonth);
      setViewYear(tempYear);
      setIsMonthYearPickerOpen(false);
    }, [tempMonth, tempYear]);

    const getDaysInMonth = (month: number, year: number) =>
      new Date(year, month + 1, 0).getDate();

    const getFirstDayOfMonth = (month: number, year: number) =>
      (new Date(year, month, 1).getDay() + 6) % 7;

    const calendarDays = useMemo(() => {
      const daysInMonth = getDaysInMonth(viewMonth, viewYear);
      const firstDay = getFirstDayOfMonth(viewMonth, viewYear);
      const days: Array<{ key: string; day: number | null }> = [];

      for (let i = 0; i < firstDay; i += 1) {
        days.push({
          key: `empty-${viewYear}-${viewMonth}-${i}`,
          day: null,
        });
      }
      for (let day = 1; day <= daysInMonth; day += 1) {
        days.push({
          key: `${viewYear}-${viewMonth}-${day}`,
          day,
        });
      }

      return days;
    }, [viewMonth, viewYear]);

    const isSelectedDate = useCallback(
      (day: number | null) => {
        if (!day || !selectedDate) return false;
        return (
          selectedDate.getDate() === day &&
          selectedDate.getMonth() === viewMonth &&
          selectedDate.getFullYear() === viewYear
        );
      },
      [selectedDate, viewMonth, viewYear],
    );

    const handleDateClick = useCallback(
      (day: number | null) => {
        if (!day) return;
        const newDate = new Date(viewYear, viewMonth, day);
        onDateSelect(newDate);
      },
      [onDateSelect, viewMonth, viewYear],
    );

    const getDayButtonClassName = useCallback(
      (day: number | null) => {
        if (isSelectedDate(day)) {
          return "bg-Color-Background-brand-default text-Color-Text-neutral-white";
        }
        if (day) {
          return "hover:bg-Color-Background-brand-inverted text-Color-Text-neutral-black";
        }
        return "text-transparent cursor-default";
      },
      [isSelectedDate],
    );

    return (
      <div ref={ref} className={cn("flex flex-col gap-4", className)}>
        <div className="flex items-center justify-between w-full">
          <button
            onClick={() => updateMonthYear("prev")}
            className="flex items-center justify-center w-6 h-6 transition-colors duration-200 hover:opacity-70"
            type="button"
          >
            <ChevronLeft className="w-4 h-4 text-Color-Icon-neutral" />
          </button>

          <button
            onClick={handleMonthYearOpen}
            disabled={!headerClickable}
            className={cn(
              "mebi-body-small-semibold text-Color-Text-neutral-black text-center w-[140px] bg-transparent border-none",
              headerClickable
                ? "cursor-pointer transition-colors duration-200 hover:opacity-70"
                : "cursor-default",
            )}
            type="button"
          >
            {MONTH_NAMES[viewMonth]} {viewYear}
          </button>

          <button
            onClick={() => updateMonthYear("next")}
            className="flex items-center justify-center w-6 h-6 transition-colors duration-200 hover:opacity-70"
            type="button"
          >
            <ChevronRight className="w-4 h-4 text-Color-Icon-neutral" />
          </button>
        </div>

        {isMonthYearPickerOpen ? (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-3">
              <WheelPicker
                options={monthOptions}
                selectedValue={String(tempMonth)}
                onValueChange={(value: string) => setTempMonth(Number(value))}
                height={200}
                itemHeight={40}
              />
              <WheelPicker
                options={yearOptions}
                selectedValue={String(tempYear)}
                onValueChange={(value: string) => setTempYear(Number(value))}
                height={200}
                itemHeight={40}
              />
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={handleMonthYearCancel}
                className="px-4 py-2 text-sm text-Color-Text-neutral-supplement hover:bg-Color-Background-neutral-hover rounded-Border_radius-small"
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={handleMonthYearConfirm}
                className="px-4 py-2 text-sm bg-Color-Background-brand-default text-Color-Text-neutral-white rounded-Border_radius-small hover:bg-Color-Background-brand-hover"
              >
                Xác nhận
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-7 gap-2">
              {["T2", "T3", "T4", "T5", "T6", "T7", "CN"].map((day) => (
                <div
                  key={day}
                  className="flex items-center justify-center w-[30px] h-[30px] mebi-body-xsmall-bold text-Color-Text-brand-default uppercase"
                >
                  {day}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-2">
              {calendarDays.map(({ day, key }) => (
                <button
                  key={key}
                  onClick={() => handleDateClick(day)}
                  disabled={!day}
                  type="button"
                  className={cn(
                    "flex items-center justify-center w-[30px] h-[30px] rounded-Border_radius-full mebi-body-xsmall-regular transition-colors duration-200",
                    getDayButtonClassName(day),
                  )}
                >
                  {day}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    );
  },
);

Calendar.displayName = "Calendar";

export { Calendar };
